import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
// The one confirmation dialog this modal has. SettingsModal also imports this tab; the cycle is
// safe because the binding is only read inside a render, after both modules have evaluated.
import { ConfirmDialog } from './SettingsModal';

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

const EMPTY_ROW = { model_prefix: '', input_rate: '', cached_input_rate: '', output_rate: '', is_default: false, notes: '' };

const LlmPricingTab = () => {
  const [pricings, setPricings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [adding, setAdding] = useState(false);
  const [newForm, setNewForm] = useState({ ...EMPTY_ROW });
  const [saving, setSaving] = useState(false);
  // The pricing row awaiting confirmation, or null. Replaces a native browser confirm.
  const [pendingDelete, setPendingDelete] = useState(null);

  const fetchPricings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/llm_pricings', { headers: { Accept: 'application/json' } });
      if (res.ok) {
        const data = await res.json();
        setPricings(data.pricings || []);
      } else {
        setError('Failed to load pricing data');
      }
    } catch (e) {
      setError('Error loading pricing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPricings(); }, [fetchPricings]);

  const handleSave = async (id, formData) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/llm_pricings/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ llm_pricing: formData })
      });
      if (res.ok) {
        const updated = await res.json();
        setPricings(prev => prev.map(p => p.id === id ? updated : p));
        setEditingId(null);
      } else {
        const data = await res.json();
        toast.error(data.errors?.join(', ') || 'Failed to save');
      }
    } catch {
      toast.error('Error saving pricing');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/admin/llm_pricings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ llm_pricing: newForm })
      });
      if (res.ok) {
        const created = await res.json();
        setPricings(prev => [...prev, created]);
        setAdding(false);
        setNewForm({ ...EMPTY_ROW });
      } else {
        const data = await res.json();
        toast.error(data.errors?.join(', ') || 'Failed to create');
      }
    } catch {
      toast.error('Error creating pricing');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pricing) => {
    setPendingDelete(null);
    try {
      const res = await fetch(`/api/admin/llm_pricings/${pricing.id}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() }
      });
      if (res.ok) {
        setPricings(prev => prev.filter(p => p.id !== pricing.id));
        if (editingId === pricing.id) setEditingId(null);
      } else {
        toast.error('Failed to delete');
      }
    } catch {
      toast.error('Error deleting pricing');
    }
  };

  const startEdit = (pricing) => {
    setEditingId(pricing.id);
    setEditForm({
      model_prefix: pricing.model_prefix,
      input_rate: pricing.input_rate,
      cached_input_rate: pricing.cached_input_rate,
      output_rate: pricing.output_rate,
      is_default: pricing.is_default,
      notes: pricing.notes || ''
    });
    setAdding(false);
  };

  const renderFormRow = (form, setForm, onSave, onCancel, key) => (
    <tr key={key}>
      <td>
        <input
          type="text"
          className="form-control form-control-sm"
          value={form.model_prefix}
          onChange={e => setForm({ ...form, model_prefix: e.target.value })}
          placeholder="gpt-5.2-pro"
          style={{ width: 140 }}
        />
      </td>
      <td>
        <input
          type="number"
          className="form-control form-control-sm"
          value={form.input_rate}
          onChange={e => setForm({ ...form, input_rate: e.target.value })}
          step="0.01"
          min="0"
          style={{ width: 80 }}
        />
      </td>
      <td>
        <input
          type="number"
          className="form-control form-control-sm"
          value={form.cached_input_rate}
          onChange={e => setForm({ ...form, cached_input_rate: e.target.value })}
          step="0.01"
          min="0"
          style={{ width: 80 }}
        />
      </td>
      <td>
        <input
          type="number"
          className="form-control form-control-sm"
          value={form.output_rate}
          onChange={e => setForm({ ...form, output_rate: e.target.value })}
          step="0.01"
          min="0"
          style={{ width: 80 }}
        />
      </td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={form.is_default || false}
          onChange={e => setForm({ ...form, is_default: e.target.checked })}
        />
      </td>
      <td>
        <input
          type="text"
          className="form-control form-control-sm"
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder="Optional notes"
          style={{ width: 160 }}
        />
      </td>
      <td>
        <button type="button" className="btn btn-sm btn-primary" onClick={onSave} disabled={saving} style={{ marginRight: 4 }}>
          {saving ? '...' : 'Save'}
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>
          Cancel
        </button>
      </td>
    </tr>
  );

  if (loading) return <p>Loading pricing data...</p>;
  if (error) return <p className="text-danger">{error}</p>;

  return (
    <div>
      <p className="text-muted" style={{ fontSize: 13, marginBottom: 12 }}>
        Pricing per 1M tokens. Verify at openai.com/api/pricing
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table className="table table-sm table-bordered" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th>Model Prefix</th>
              <th>Input $/M</th>
              <th>Cached $/M</th>
              <th>Output $/M</th>
              <th style={{ textAlign: 'center' }}>Default</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pricings.map(p => (
              editingId === p.id
                ? renderFormRow(editForm, setEditForm, () => handleSave(p.id, editForm), () => setEditingId(null), `edit-${p.id}`)
                : (
                  <tr key={p.id}>
                    <td style={{ fontFamily: 'monospace' }}>{p.model_prefix}</td>
                    <td>${p.input_rate}</td>
                    <td>${p.cached_input_rate}</td>
                    <td>${p.output_rate}</td>
                    <td style={{ textAlign: 'center' }}>{p.is_default ? 'Yes' : ''}</td>
                    <td className="text-muted">{p.notes}</td>
                    <td>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => startEdit(p)} style={{ marginRight: 4 }}>
                        Edit
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setPendingDelete(p)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                )
            ))}
            {adding && renderFormRow(newForm, setNewForm, handleCreate, () => { setAdding(false); setNewForm({ ...EMPTY_ROW }); }, 'new')}
          </tbody>
        </table>
      </div>

      {!adding && (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => { setAdding(true); setEditingId(null); }}
          style={{ marginTop: 8 }}
        >
          + Add Model
        </button>
      )}

      {pendingDelete && (
        <ConfirmDialog
          title="Delete this pricing entry?"
          body={`This removes the pricing row for ${pendingDelete.model_prefix}. Existing cost reports are not recalculated.`}
          cancelLabel="Cancel"
          confirmLabel="Delete"
          danger
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => handleDelete(pendingDelete)}
        />
      )}
    </div>
  );
};

export default LlmPricingTab;
